// Angular
import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation,} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
// RxJS
import {Observable, Subject} from 'rxjs';
import {finalize, takeUntil, tap} from 'rxjs/operators';
// Translate
import {TranslateService} from '@ngx-translate/core';
// Store
import {Store} from '@ngrx/store';
import {AppState} from '../../../../core/reducers';
// Auth
import {AuthNoticeService, AuthService, Login} from '../../../../core/auth';

@Component({
    selector: 'kt-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class LoginComponent implements OnInit, OnDestroy {
    // Public params
    loginForm: FormGroup;
    loading = false;
    isLoggedIn$: Observable<boolean>;
    errors: any = [];

    private unsubscribe: Subject<any>;
    private returnUrl: any;

    constructor(
        private router: Router,
        private auth: AuthService,
        private authNoticeService: AuthNoticeService,
        private translate: TranslateService,
        private store: Store<AppState>,
        private fb: FormBuilder,
        private cdr: ChangeDetectorRef,
        private route: ActivatedRoute
    ) {
        this.unsubscribe = new Subject();
    }

    ngOnInit(): void {
        this.initLoginForm();

        // redirect back to the returnUrl before login
        this.route.queryParams.subscribe((params) => {
            this.returnUrl = [params['returnUrl'] || '/'];
        });
    }

    ngOnDestroy(): void {
        this.authNoticeService.setNotice(null);
        this.unsubscribe.next();
        this.unsubscribe.complete();
        this.loading = false;
    }

    initLoginForm() {
        // demo message to show
        if (!this.authNoticeService.onNoticeChanged$.getValue()) {
            const initialNotice = `Acceso Gigot`;
            this.authNoticeService.setNotice(initialNotice, 'info');
        }

        this.loginForm = this.fb.group({
            email: [
                null,
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(320),
                ]),
            ],
            password: [
                null,
                Validators.compose([
                    Validators.required,
                    Validators.minLength(3),
                    Validators.maxLength(100),
                ]),
            ],
        });
    }

    submit() {
        const controls = this.loginForm.controls;
        /** check form */
        if (this.loginForm.invalid) {
            Object.keys(controls).forEach((controlName) =>
                controls[controlName].markAsTouched()
            );
            return;
        }

        this.loading = true;

        const authData = {
            email: controls['email'].value,
            password: controls['password'].value,
        };
        this.auth
            .login(authData.email, authData.password)
            .pipe(
                tap(
                    (user) => {
                        if (user) {
                            this.store.dispatch(
                                new Login({authToken: user.token})
                            );
                            this.router.navigate(this.returnUrl); // Main page
                        } else {
                            this.authNoticeService.setNotice(
                                this.translate.instant(
                                    'AUTH.VALIDATION.INVALID_LOGIN'
                                ),
                                'danger'
                            );
                        }
                    },
                    (error) => {
                        // TODO: Implementar error handling global
                        if (error.status === 401) {
                            this.authNoticeService.setNotice(
                                this.translate.instant(
                                    'AUTH.VALIDATION.INVALID_LOGIN'
                                ),
                                'danger'
                            );
                        }
                        if (error.status === 403) {
                            this.authNoticeService.setNotice(
                                this.translate.instant(
                                    'No está autorizado para realizar esta acción'
                                ),
                                'danger'
                            );
                        } else {
                            this.authNoticeService.setNotice(
                                this.translate.instant(
                                    'AUTH.VALIDATION.INVALID_LOGIN'
                                ),
                                'danger'
                            );
                        }
                    }
                ),
                takeUntil(this.unsubscribe),
                finalize(() => {
                    this.loading = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe();
    }

    isControlHasError(controlName: string, validationType: string): boolean {
        const control = this.loginForm.controls[controlName];
        if (!control) {
            return false;
        }

        const result =
            control.hasError(validationType) &&
            (control.dirty || control.touched);
        return result;
    }
}
